import React, { useCallback, useEffect, useRef, useState } from 'react';
import { alert } from 'equasion-lib';
import { FiAlertTriangle, FiCheckCircle, FiSkipForward, FiX } from 'react-icons/fi';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import useZoneScanLookup from '@/lib/shared-coms/ZoneScanner/useZoneScanLookup';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { SCAN_PROFILE, useScanner } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { playSuccessTone } from '@/lib/scanning/feedback';
import { scanAlert } from '@/lib/scanning/scanAlert';
import {
  manualPickVerifyUnit,
  type PickVerifyUnitInput,
  type ProductScanLike
} from '@/lib/scanning/retailCode';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import PickGrabPanel from '../PickGrabPanel/PickGrabPanel';
import PickManualEntry, {
  type PickManualUnitFields
} from '../PickManualEntry/PickManualEntry';
import PickQuarantinePanel from '../PickQuarantinePanel/PickQuarantinePanel';
import PickUnitScanPanel from '../PickUnitScanPanel/PickUnitScanPanel';
import PickUpcLinkPanel from '../PickUpcLinkPanel/PickUpcLinkPanel';
import { upcLinkOffer, type UpcLinkOffer } from '@/lib/scanning/productUpcLink';
import { useRootStore } from '@/lib/providers/RootProvider';
import {
  classifyPickScan,
  classifyQuarantineZoneLookup,
  classifyZoneLookup,
  openPickSheet,
  pickDate,
  pickDuplicateSerialMessage,
  pickLineNeedsClose,
  pickLineProduct,
  pickLineScansUnits,
  pickOverScanMessage,
  pickScannedCount,
  pickSerialRequiredMessage,
  pickUnitScanKey,
  pickQuarantineOutcome,
  pickScanProfileKey,
  pickSheetReducer,
  pickSkipOutcome,
  pickSkippedZonesLabel,
  pickStopPartLabel,
  pickZoneAcceptedNote,
  pickZoneRefusalCopy,
  pickZoneShare,
  pickZoneShortOutcome,
  type PickActionResult,
  type PickConfirmLineArgs,
  type PickLine,
  type PickQuarantineDamagedArgs,
  type PickQuarantineMissingArgs,
  type PickQuarantineResult,
  type PickScanProfileKey,
  type PickScanUnitArgs,
  type PickSheetAction,
  type PickSheetState,
  type PickWalkStop
} from '../../pickLineSteps';

type AlertIcon = 'success' | 'error' | 'warning' | 'info' | 'question';

const SCAN_PROFILE_BY_KEY: Record<PickScanProfileKey, string> = {
  zone: SCAN_PROFILE.ZONE,
  item: SCAN_PROFILE.ITEM,
  product: SCAN_PROFILE.PRODUCT
};

const STEPS: { key: PickSheetState['step']; label: string }[] = [
  { key: 'zone', label: 'Zone' },
  { key: 'unit', label: 'One unit' },
  { key: 'grab', label: 'Grab the rest' }
];

/** A unit-scan (controlled) line: the first unit, then every other one scanned — nothing is grabbed. */
const UNIT_SCAN_STEPS: { key: PickSheetState['step']; label: string }[] = [
  { key: 'zone', label: 'Zone' },
  { key: 'unit', label: 'First unit' },
  { key: 'scan', label: 'Scan each unit' }
];

/** The camera re-arms quickly while units are scanned one after another (Verify's pace). */
const UNIT_SCAN_REARM_DELAY_MS = 250;

type PickLineSheetProps = {
  // Declared because this app's JSX typings carry no IntrinsicAttributes (see PickLineRow).
  key?: string;
  /** The line as picked at this stop (pickLineAtStop): the stop's zone is the suggestion. */
  line: PickLine;
  /** The walk stop the sheet was opened at, while it is still on the walk (null once confirmed). */
  stop?: PickWalkStop | null;
  /** The zone the sheet was opened at — kept after its stop leaves the walk. */
  stopZoneId?: string | null;
  /** The whole walk, to say where the line's other zones are. */
  stops?: readonly PickWalkStop[] | null;
  companyId: string;
  onClose: () => void;
  onConfirmLine: (args: PickConfirmLineArgs) => Promise<PickActionResult>;
  /**
   * The whole-line short: from an older server without per-zone stops — and a unit-scan line's
   * "Record short pick" (its units scanned, with the reason).
   */
  onMarkShort: (
    orderItemId: string,
    foundQuantity: number,
    options?: { blocking?: boolean; reason?: string | null }
  ) => Promise<PickActionResult>;
  /** A unit-scan (controlled) line: one more unit scanned at a confirmed zone. */
  onScanUnit?: (args: PickScanUnitArgs) => Promise<PickActionResult>;
  /** A unit-scan (controlled) line: take a scanned unit back off (that serial, or the last). */
  onRemoveUnit?: (orderItemId: string, serialNumber?: string | null) => Promise<PickActionResult>;
  /** "Couldn't find them all here": the zone gave fewer than its share. */
  onMarkStopShort?: (
    orderItemId: string,
    zoneId: string,
    foundQuantity: number
  ) => Promise<PickActionResult>;
  /** "Zone is empty — skip it": the zone's share moves on to the line's next FEFO zone. */
  onSkipZone: (orderItemId: string, zoneId: string) => Promise<PickActionResult>;
  /** Quarantine → Missing: the zone's stock of the line goes to a quarantine zone. */
  onQuarantineMissing: (args: PickQuarantineMissingArgs) => Promise<PickQuarantineResult>;
  /** Quarantine → Damaged: the scanned unit(s) are quarantined where they are. */
  onQuarantineDamaged: (args: PickQuarantineDamagedArgs) => Promise<PickQuarantineResult>;
  /**
   * A quarantine left the line no pickable stock: the page offers to close the line (mark it short).
   * Without it, the picker is only told, and marks the line short from its row.
   */
  onNoStockLeft?: (line: PickLine, copy: { title: string; text: string }) => void;
};

/**
 * The per-line pick, as an in-page full-screen sheet (not react-modal: the sheet owns the camera
 * while open, and the page's own manual-entry panel has to sit over it). ONE SharedScanner, whose
 * decoder profile follows the step: ZONE while the rack label is expected, then ITEM (serialized
 * DataMatrix) or PRODUCT (retail barcode) for the unit, kept through step 3 where every scan is
 * ignored — the unit that just matched is usually still in frame.
 *
 * The sheet picks ONE ZONE STOP of the line — the one the row sent the picker to. The zone shown
 * is the stop's; any zone holding eligible stock of the line (and not already picked for it) is
 * accepted, with a soft note (no fail tone) when it is not the suggested one. Step 3 grabs THAT
 * zone's share only: the line's other zones are rows of their own further along the walk, and the
 * panel says where.
 *
 * Tones follow the floor convention: a success chime only once the server accepted (zone lookup
 * resolved to a zone of the line / the line confirmed), the fail tone with every scan refusal, and
 * nothing for typed entry (`say = isScan ? scanAlert : alert`, as on the standard pick screens).
 *
 * The stock records can say a zone holds the product when the shelf is empty. Steps 1 and 2 offer
 * "<zone> is empty — skip to the next zone": the server leaves that zone out of the line's
 * allocation, its share becomes a stop wherever the walk reaches the next zone holding it, and the
 * sheet closes saying where (or that no other zone has it, so the line can be marked short). A zone
 * with FEWER than its share is step 3's "Couldn't find them all here": the zone records what it had
 * and the rest moves on the same way.
 *
 * Step 2 also offers "Quarantine" for the zone just scanned (never step 1: Missing moves every unit
 * recorded in the zone, so the picker first proves where they are): Missing (none of the product is
 * there — every unit recorded in the zone goes to a quarantine zone, and the line moves on exactly
 * as after a skip) or Damaged (the scanned unit is quarantined where it is, and the picker picks a
 * good one). While its panel is open the camera reads for it — the quarantine zone's label, or the
 * damaged unit.
 *
 * A UNIT-SCAN (controlled) line never grabs: once its first unit is accepted the sheet moves to
 * "Scan each unit", the camera stays live, and every 2D scan counts one unit (PickUnitScanPanel).
 * The device answers duplicates, over-scans and linear barcodes on the spot with the fail tone, as
 * the server would; a camera re-read of the pack just counted is ignored. Opened from the row of a
 * line part-way through a zone, the sheet resumes there at "Scan each unit".
 */
function PickLineSheet({
  line,
  stop = null,
  stopZoneId = null,
  stops = null,
  companyId,
  onClose,
  onConfirmLine,
  onMarkShort,
  onMarkStopShort,
  onScanUnit,
  onRemoveUnit,
  onSkipZone,
  onQuarantineMissing,
  onQuarantineDamaged,
  onNoStockLeft
}: PickLineSheetProps) {
  const { discardScanResult } = useScanner() || {};
  const lookupZoneCode = useZoneScanLookup({ companyId });
  const unitScan = pickLineScansUnits(line);
  const getPermission = useRootStore((state) => state.getPermission) as (
    permission: string
  ) => unknown;
  const canLinkUpc = !!getPermission('products:update');
  // "UPC not recognized" on an OTC line: the offer to link the scanned UPC, and the unit to re-send.
  const [upcLink, setUpcLink] = useState<{ offer: UpcLinkOffer; unit: PickVerifyUnitInput } | null>(
    null
  );
  const [state, setState] = useState<PickSheetState>(() =>
    // A unit-scan line reopened at a zone already confirmed for it carries on scanning there.
    openPickSheet(
      line.orderItemId,
      unitScan && !stop && stopZoneId
        ? (line.confirmedStops || []).find((confirmed) => confirmed.zone?.id === stopZoneId)
            ?.zone ?? null
        : null
    )
  );
  // The quarantine zone has to be at the emptied zone's location: the lookup is scoped to it.
  const lookupQuarantineZoneCode = useZoneScanLookup({
    companyId,
    locationId: state.quarantine?.zone.locationId || undefined
  });

  // The step machine is read synchronously by the scan handler — two trigger pulls can land before
  // React re-renders — so the ref is the source of truth and the state only re-renders the sheet.
  const stateRef = useRef(state);
  const apply = useCallback((action: PickSheetAction) => {
    const next = pickSheetReducer(stateRef.current, action) ?? stateRef.current;
    if (next === stateRef.current) return;
    stateRef.current = next;
    setState(next);
  }, []);
  const requestIdRef = useRef(0);
  // Set while a skip or a quarantine is in flight: its answer moves the line's zones on purpose,
  // which the "Zone changed" effect below must not report as a surprise.
  const skippingRef = useRef(false);
  const lineRef = useRef(line);
  lineRef.current = line;
  const mountedRef = useRef(true);

  // A scan published while no screen was listening (the camera keeps running on the list) must not
  // be handed to this sheet as its first zone scan, and one left unconsumed at close must not reach
  // the next sheet — ZoneScanOverlay's rule for handing the camera back and forth.
  useEffect(() => {
    mountedRef.current = true;
    discardScanResult?.();
    return () => {
      mountedRef.current = false;
      discardScanResult?.();
    };
    // Mount / unmount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The accepted zone no longer holds eligible stock of the line (a fresher list): the accepted
  // label is stale. A suggestion that merely moved elsewhere is no reason to re-scan — the zone the
  // picker is standing at is still one the line may be picked from.
  const expectedZoneId = line.expectedZone?.id;
  const eligibleZoneIds = (line.eligibleZones || []).map((zone) => zone.id).join(',');
  useEffect(() => {
    const current = stateRef.current;
    if (skippingRef.current) return;
    if (current.step !== 'unit' || !current.acceptedZone || !expectedZoneId) return;
    if (current.acceptedZone.id === expectedZoneId) return;
    if (eligibleZoneIds.split(',').includes(current.acceptedZone.id)) return;
    apply({ type: 'rezone' });
    alert(
      'Zone changed',
      `${current.acceptedZone.name} no longer has pickable stock of this line. Suggested: ${line.expectedZone?.name} — scan a zone label.`,
      6,
      'warning'
    );
  }, [apply, eligibleZoneIds, expectedZoneId, line.expectedZone?.name]);

  // A fresher list says this zone is no longer to pick (the page re-reads after a refusal or a lost
  // answer): follow the server instead of asking for a scan it would refuse as "already picked".
  // The line picked — or THIS zone of it confirmed — means a confirm committed but its answer never
  // arrived: the unit is recorded, so on to grab the zone's share (Done closes, and the row offers
  // Undo). Short means nothing is left to pick here: close. Only once no request is in flight — its
  // own answer settles the step first — so this re-runs whenever one settles.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const lineStatus = line.status;
  const confirmedZoneIds = (line.confirmedStops || [])
    .map((confirmed) => confirmed.zone?.id)
    .filter(Boolean)
    .join(',');
  useEffect(() => {
    const current = stateRef.current;
    if (current.pendingRequestId !== null) return;
    if (lineStatus === 'short') {
      onCloseRef.current();
      return;
    }
    if (current.step === 'grab' || current.step === 'scan') return;
    const zoneId = current.acceptedZone?.id ?? stopZoneId;
    const zoneConfirmed = !!zoneId && confirmedZoneIds.split(',').includes(zoneId);
    if (lineStatus !== 'picked' && !zoneConfirmed) return;
    const scansUnits = pickLineScansUnits(lineRef.current);
    apply({ type: 'pickedOnServer', unitScan: scansUnits });
    alert(
      lineStatus === 'picked' ? 'Line already picked' : 'Zone already picked',
      scansUnits
        ? `${lineRef.current.productName}: ${pickScannedCount(lineRef.current)} of ${lineRef.current.quantity} units are recorded as scanned. Scan the rest here, or close and Undo on the list.`
        : lineStatus === 'picked'
        ? `${lineRef.current.productName} is recorded as picked. Grab the rest, or close and Undo it on the list.`
        : `${lineRef.current.productName} is recorded as picked here. Grab this zone's share, or close and Undo the zone on the list.`,
      6,
      'info'
    );
  }, [
    apply,
    confirmedZoneIds,
    lineStatus,
    state.pendingRequestId,
    state.step,
    stopZoneId
  ]);

  const expectedZoneName = line.expectedZone?.name || 'the suggested zone';
  const say =
    (isScan: boolean) =>
    (title: string, text: string, seconds = 6, icon: AlertIcon = 'error') =>
      isScan ? scanAlert(title, text, seconds, icon) : alert(title, text, seconds, icon);

  const submitZone = async (
    code: string,
    { isScan, productLike }: { isScan: boolean; productLike: boolean }
  ) => {
    const requestId = ++requestIdRef.current;
    apply({ type: 'zoneLookupStarted', requestId });
    if (stateRef.current.pendingRequestId !== requestId) return;
    const lookup = await lookupZoneCode(code);
    if (!mountedRef.current) return;
    const outcome = classifyZoneLookup(lookup, lineRef.current);
    if (outcome.kind === 'accepted') {
      apply({ type: 'zoneAccepted', requestId, zone: outcome.zone, code });
      if (isScan) playSuccessTone();
      const note = pickZoneAcceptedNote(outcome, expectedZoneName);
      if (note) alert(note.title, note.text, 4, 'info');
      return;
    }
    apply({ type: 'zoneRejected', requestId });
    if (outcome.kind === 'lookupFailed') {
      say(isScan)(
        'Zone lookup failed',
        operatorFacingMessage(
          outcome.error,
          'Could not look up that zone. Please try again.'
        )
      );
      return;
    }
    const copy = pickZoneRefusalCopy(outcome, {
      code,
      expectedName: expectedZoneName,
      productLike
    });
    say(isScan)(copy.title, copy.text, 6, productLike ? 'warning' : 'error');
  };

  const submitUnit = async (
    unit: PickVerifyUnitInput,
    { isScan, key = null }: { isScan: boolean; key?: string | null }
  ) => {
    const requestId = ++requestIdRef.current;
    setUpcLink(null);
    apply({ type: 'unitStarted', requestId });
    const started = stateRef.current;
    if (started.pendingRequestId !== requestId || !started.acceptedZone) {
      if (started.pendingRequestId === requestId)
        apply({ type: 'unitRejected', requestId });
      return;
    }
    const result = await onConfirmLine({
      orderItemId: lineRef.current.orderItemId,
      zoneId: started.acceptedZone.id,
      unit
    });
    if (!mountedRef.current) return;
    if (result.ok) {
      apply({
        type: 'unitAccepted',
        requestId,
        unitScan: pickLineScansUnits(lineRef.current),
        scanned: key ? { key, at: Date.now() } : null
      });
      if (isScan) playSuccessTone();
      return;
    }
    apply({ type: 'unitRejected', requestId });
    // A handled refusal already took the picker somewhere (lost order, lost claim); this one is about
    // the unit — the wrong product — so stay on step 2 and say why.
    if (result.handled) return;
    const offer = upcLinkOffer(result.reason, unit, lineRef.current, canLinkUpc);
    if (offer) {
      if (isScan) scanAlert('UPC not recognized', result.message, 3, 'warning');
      setUpcLink({ offer, unit });
      return;
    }
    say(isScan)('Line not confirmed', result.message);
  };

  // Step 3's zone: the one the unit was just confirmed at (or, after a lost answer, the stop's).
  const grabZoneId = state.acceptedZone?.id ?? stopZoneId;

  // ── a unit-scan (controlled) line: every unit scanned, never grabbed or typed ──

  /** One more unit at the zone being scanned. The count moves only on the server's answer. */
  const submitScan = async (unit: PickVerifyUnitInput, key: string | null) => {
    if (!onScanUnit) return;
    const requestId = ++requestIdRef.current;
    apply({ type: 'scanStarted', requestId });
    if (stateRef.current.pendingRequestId !== requestId) return;
    const result = await onScanUnit({
      orderItemId: lineRef.current.orderItemId,
      zoneId: stateRef.current.acceptedZone?.id ?? stopZoneId,
      unit
    });
    if (!mountedRef.current) return;
    apply({
      type: 'scanSettled',
      requestId,
      scanned: result.ok && key ? { key, at: Date.now() } : null
    });
    if (result.ok) {
      playSuccessTone();
      const next = result.list?.lines?.find((l) => l.orderItemId === lineRef.current.orderItemId);
      if (next?.status === 'picked') {
        alert('All units scanned', `${next.productName}: ${next.quantity} of ${next.quantity} scanned.`, 3, 'success');
      }
      return;
    }
    if (!result.handled) scanAlert('Unit not counted', result.message);
  };

  /** "Undo last scan" (no serial) or a serial's ✕: the unit comes back off the line, logged. */
  const removeScannedUnit = async (serialNumber: string | null) => {
    if (!onRemoveUnit) return;
    const requestId = ++requestIdRef.current;
    apply({ type: 'scanStarted', requestId });
    if (stateRef.current.pendingRequestId !== requestId) return;
    const result = await onRemoveUnit(lineRef.current.orderItemId, serialNumber);
    if (!mountedRef.current) return;
    apply({ type: 'scanSettled', requestId });
    if (!result.ok && !result.handled) alert('Could not remove the unit', result.message, 6, 'error');
  };

  /** "Couldn't find them all here": the zone gave the units scanned at it; the rest moves on. */
  const zoneShortFromScans = () => {
    const here = (lineRef.current.confirmedStops || []).find(
      (confirmed) => confirmed.zone?.id === (stateRef.current.acceptedZone?.id ?? stopZoneId)
    );
    void submitShort(here?.scannedQuantity ?? 0);
  };

  /** "Record short pick": the units scanned are what is picked, with the picker's reason. */
  const recordShort = async (reason: string) => {
    const requestId = ++requestIdRef.current;
    apply({ type: 'shortStarted', requestId });
    if (stateRef.current.pendingRequestId !== requestId) return;
    const result = await onMarkShort(lineRef.current.orderItemId, pickScannedCount(lineRef.current), {
      blocking: false,
      reason
    });
    if (!mountedRef.current) return;
    apply({ type: 'shortSettled', requestId });
    if (result.ok) {
      apply({ type: 'closeShortEntry' });
      alert(
        'Short pick recorded',
        `${lineRef.current.productName} is short — flagged for review when Pick is completed.`,
        6,
        'warning'
      );
      onClose();
      return;
    }
    if (!result.handled) alert('Could not record short', result.message, 6, 'error');
  };

  // "Couldn't find them all here": at a zone of a line picked zone by zone, the zone records what it
  // had and the rest moves to the line's other zones (or the line goes short). From an older server
  // without per-zone stops, the whole line is marked short as before.
  const submitShort = async (foundQuantity: number) => {
    const requestId = ++requestIdRef.current;
    apply({ type: 'shortStarted', requestId });
    if (stateRef.current.pendingRequestId !== requestId) return;
    const { orderItemId } = lineRef.current;
    const zoneId = stateRef.current.acceptedZone?.id ?? stopZoneId;
    const perZone =
      !!onMarkStopShort && !!zoneId && Array.isArray(lineRef.current.confirmedStops);
    const result = perZone
      ? await onMarkStopShort!(orderItemId, zoneId!, foundQuantity)
      : await onMarkShort(orderItemId, foundQuantity, { blocking: false });
    if (!mountedRef.current) return;
    apply({ type: 'shortSettled', requestId });
    if (result.ok) {
      if (perZone) {
        // The answer's line, not lineRef: React has not re-rendered with it yet.
        const next =
          result.list?.lines?.find((l) => l.orderItemId === orderItemId) ?? null;
        const zoneName =
          stateRef.current.acceptedZone?.name ||
          lineRef.current.expectedZone?.name ||
          'This zone';
        const outcome = pickZoneShortOutcome(zoneName, next, result.list?.stops ?? null);
        alert(
          outcome.title,
          outcome.text,
          8,
          next?.status === 'short' ? 'warning' : 'info'
        );
      }
      onClose();
      return;
    }
    if (!result.handled) alert('Could not record short', result.message, 6, 'error');
  };

  const skipZone = async (
    zone: { id: string | null; name: string } | null | undefined
  ) => {
    if (!zone?.id) return;
    const requestId = ++requestIdRef.current;
    apply({ type: 'skipStarted', requestId });
    if (stateRef.current.pendingRequestId !== requestId) return;
    const { orderItemId } = lineRef.current;
    skippingRef.current = true;
    let result: PickActionResult;
    try {
      result = await onSkipZone(orderItemId, zone.id);
    } finally {
      skippingRef.current = false;
    }
    if (!mountedRef.current) return;
    apply({ type: 'skipSettled', requestId, skipped: result.ok });
    if (!result.ok) {
      if (!result.handled) alert('Could not skip the zone', result.message, 6, 'error');
      return;
    }
    // The answer's line, not lineRef: React has not re-rendered with it yet. The zone's share is now
    // a stop wherever the walk reaches the next zone holding it — the picker carries on along the
    // walk, so the sheet closes and says where (never "walk over there now").
    const next = result.list?.lines?.find((l) => l.orderItemId === orderItemId) ?? null;
    const outcome = pickSkipOutcome(zone.name, next, result.list?.stops ?? null);
    alert(
      outcome.title,
      outcome.text,
      8,
      outcome.kind === 'noStock' ? 'warning' : 'info'
    );
    onClose();
  };

  const submitQuarantineZone = async (code: string) => {
    const requestId = ++requestIdRef.current;
    apply({ type: 'quarantineLookupStarted', requestId });
    if (stateRef.current.pendingRequestId !== requestId) return;
    const lookup = await lookupQuarantineZoneCode(code);
    if (!mountedRef.current) return;
    apply({ type: 'quarantineLookupSettled', requestId });
    const outcome = classifyQuarantineZoneLookup(lookup);
    if (outcome.kind === 'accepted') {
      apply({ type: 'quarantineZoneChosen', zone: outcome.zone });
      playSuccessTone();
      return;
    }
    if (outcome.kind === 'lookupFailed') {
      scanAlert(
        'Zone lookup failed',
        operatorFacingMessage(outcome.error, 'Could not look up that zone. Please try again.')
      );
      return;
    }
    if (outcome.kind === 'notQuarantine') {
      scanAlert(
        'Not a quarantine zone',
        `${outcome.zone?.name || `“${code}”`} is not a quarantine zone. Scan a quarantine zone's label, or choose it.`
      );
      return;
    }
    scanAlert(
      'Zone not found',
      `No zone at this location matches “${code}”. Scan a quarantine zone's label, or choose it.`
    );
  };

  const submitQuarantine = async (quantity: number | null = null) => {
    const current = stateRef.current.quarantine;
    if (!current || current.mode === 'choose') return;
    if (current.mode === 'missing' && !current.quarantineZone) return;
    if (current.mode === 'damaged' && !current.unit) return;
    const requestId = ++requestIdRef.current;
    apply({ type: 'quarantineStarted', requestId });
    if (stateRef.current.pendingRequestId !== requestId) return;
    const { orderItemId } = lineRef.current;
    skippingRef.current = true;
    let result: PickQuarantineResult;
    try {
      result =
        current.mode === 'missing'
          ? await onQuarantineMissing({
              orderItemId,
              zoneId: current.zone.id,
              quarantineZoneId: current.quarantineZone!.id
            })
          : await onQuarantineDamaged({
              orderItemId,
              zoneId: current.zone.id,
              unit: current.unit!,
              quantity
            });
    } finally {
      skippingRef.current = false;
    }
    if (!mountedRef.current) return;
    apply({ type: 'quarantineSettled', requestId, done: result.ok });
    if (!result.ok) {
      if (!result.handled) alert('Not quarantined', result.message, 8, 'error');
      return;
    }
    // The answer's line, not lineRef: React has not re-rendered with it yet.
    const next = result.list?.lines?.find((l) => l.orderItemId === orderItemId) ?? null;
    const outcome = pickQuarantineOutcome(result.quarantine, next, result.list?.stops ?? null);
    if (outcome.kind === 'stay') {
      alert(outcome.title, outcome.text, 6, 'success');
      return;
    }
    // Nothing pickable is left (Missing emptied the last zone, or the damaged unit was the last good
    // one): the page offers to close the line instead of a toast the picker acts on from the row.
    if (outcome.kind === 'noStock' && next && pickLineNeedsClose(next) && onNoStockLeft) {
      onNoStockLeft(next, outcome);
      onClose();
      return;
    }
    // Missing emptied the zone: like a skip, the line's share is now picked wherever the walk next
    // reaches it, so the sheet closes and says where.
    alert(outcome.title, outcome.text, 8, outcome.kind === 'noStock' ? 'warning' : 'info');
    onClose();
  };

  const handleScan = (result: ProductScanLike) => {
    const current = lineRef.current;
    const decision = classifyPickScan(stateRef.current, current, result);
    switch (decision.kind) {
      case 'ignore':
        return;
      case 'productInZoneStep':
        scanAlert(
          'That is a product barcode',
          `Scan the zone label first — suggested zone: ${expectedZoneName}.`,
          5,
          'warning'
        );
        return;
      case 'lookupZone':
        submitZone(decision.code, { isScan: true, productLike: decision.productLike });
        return;
      case 'notProduct':
        // Soft: most often the rack label or a tote label caught in frame. No server call, no fail tone.
        alert(
          'Scan the product',
          `That is not a product barcode. Scan one unit of ${current.productName}.`,
          4,
          'info'
        );
        return;
      case 'scan2D':
        alert(
          'Scan the 2D barcode',
          `Scan the 2D barcode on ${current.productName} — it carries the serial number to quarantine.`,
          4,
          'info'
        );
        return;
      case 'confirmUnit':
        submitUnit(decision.unit, { isScan: true, key: pickUnitScanKey(result) });
        return;
      case 'scanUnit':
        submitScan(decision.unit, decision.key);
        return;
      case 'needs2D':
        scanAlert('Scan the 2D barcode', pickSerialRequiredMessage(current.productName));
        return;
      case 'duplicateSerial':
        scanAlert('Duplicate serial', pickDuplicateSerialMessage(decision.serialNumber));
        return;
      case 'overScan':
        scanAlert('Over-scan', pickOverScanMessage(current));
        return;
      case 'productInQuarantineZoneStep':
        scanAlert(
          'That is a product barcode',
          "Scan the quarantine zone's label, or choose it from the list.",
          5,
          'warning'
        );
        return;
      case 'lookupQuarantineZone':
        submitQuarantineZone(decision.code);
        return;
      case 'damagedUnit':
        // Shown on the panel; nothing is quarantined until the picker taps the button.
        apply({ type: 'damagedUnitCaptured', unit: decision.unit });
        return;
      default:
    }
  };

  const submitManualUnit = ({ code, expiration }: PickManualUnitFields) =>
    submitUnit(manualPickVerifyUnit({ code, expiration }), { isScan: false });

  const busy = state.pendingRequestId !== null;
  const skippedLabel = pickSkippedZonesLabel(line);
  const serialized = line.kind === 'SERIALIZED';
  const atSuggestedZone =
    !state.acceptedZone || state.acceptedZone.id === line.expectedZone?.id;
  // The suggested expiration is FEFO's in the suggested zone; elsewhere there is nothing to suggest.
  const expectedExpiration = atSuggestedZone ? pickDate(line.expectedExpiration) : null;
  // Step 2's "empty" is about the zone the picker is standing at, which need not be the suggestion.
  const skipTarget =
    state.step === 'unit' && state.acceptedZone ? state.acceptedZone : line.expectedZone;
  const steps = unitScan ? UNIT_SCAN_STEPS : STEPS;
  const stepIndex = steps.findIndex(({ key }) => key === state.step);
  // What is picked at THIS zone: the stop's share while it is on the walk, then the share recorded
  // for the zone once it is confirmed.
  const partLabel = pickStopPartLabel(stop);
  const sheetQuantity =
    state.step === 'grab' || state.step === 'scan'
      ? pickZoneShare(line, grabZoneId)
      : stop?.quantity ?? line.quantity;

  return (
    <div
      className="pick-sheet"
      role="dialog"
      aria-modal="true"
      aria-label={`Pick ${line.productName}`}
    >
      <div className="pick-sheet__top">
        <button
          type="button"
          className="pick-sheet__close"
          aria-label="Close"
          onClick={onClose}
        >
          <FiX />
        </button>
        <div className="pick-sheet__product">
          <WarehouseProductDisplay product={pickLineProduct(line)} compact>
            {line.isShortDated && (
              <span className="pick-line__badge pick-line__badge--short-dated">
                SHORT DATED
              </span>
            )}
          </WarehouseProductDisplay>
        </div>
        <div className="pick-sheet__qty">
          <small>{partLabel ? 'Here' : 'Qty'}</small>
          {sheetQuantity}
          {sheetQuantity !== line.quantity && (
            <small className="pick-sheet__qty-of">of {line.quantity}</small>
          )}
        </div>
      </div>

      <ol className="pick-sheet__steps">
        {steps.map(({ key, label }, index) => (
          <li
            key={key}
            className={[
              index < stepIndex && 'is-done',
              index === stepIndex && 'is-active'
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span>{index + 1}</span> {label}
          </li>
        ))}
      </ol>

      <div className="pick-sheet__body">
        <div className="pick-sheet__prompt">
          {upcLink && state.step === 'unit' && !state.quarantine && (
            <PickUpcLinkPanel
              offer={upcLink.offer}
              companyId={companyId}
              onLinked={() => {
                const { unit } = upcLink;
                setUpcLink(null);
                void submitUnit(unit, { isScan: false });
              }}
              onCancel={() => setUpcLink(null)}
            />
          )}
          {state.quarantine && (
            <PickQuarantinePanel
              line={line}
              companyId={companyId}
              quarantine={state.quarantine}
              busy={busy}
              onChoose={(mode) => apply({ type: 'chooseQuarantine', mode })}
              onBack={() => apply({ type: 'quarantineBack' })}
              onClose={() => apply({ type: 'closeQuarantine' })}
              onQuarantineZoneChosen={(zone) => apply({ type: 'quarantineZoneChosen', zone })}
              onQuarantineZoneScanning={(scanning) =>
                apply({ type: 'quarantineZoneScanning', scanning })
              }
              onDamagedUnitTyped={(unit) => apply({ type: 'damagedUnitCaptured', unit })}
              onSubmitMissing={() => submitQuarantine()}
              onSubmitDamaged={(quantity) => submitQuarantine(quantity)}
            />
          )}
          {!state.quarantine && state.step === 'zone' && (
            <>
              <h2>
                Scan zone <strong>{expectedZoneName}</strong>
              </h2>
              {partLabel && (
                <p className="pick-sheet__part">
                  {partLabel} for this line — take {stop!.quantity} here.
                </p>
              )}
              <p>
                Suggested zone — any zone holding this product is OK. If the label will
                not scan, use Manual Entry and type its name.
              </p>
            </>
          )}
          {!state.quarantine && state.step === 'unit' && (
            <>
              <p className="pick-sheet__zone-ok">
                <FiCheckCircle aria-hidden /> Zone{' '}
                {state.acceptedZone?.name || expectedZoneName}
              </p>
              <h2>
                Scan {unitScan ? 'the first' : 'one'} unit of <strong>{line.productName}</strong>
              </h2>
              <p>
                {serialized
                  ? `Scan the 2D barcode.${
                      expectedExpiration
                        ? ` Suggested expiration: ${expectedExpiration}.`
                        : ''
                    }`
                  : 'Scan the barcode on the pack (UPC or NDC).'}
                {unitScan &&
                  ` Controlled substance: every unit is scanned — ${line.quantity} in all.`}
              </p>
            </>
          )}
          {!state.quarantine && state.step === 'scan' && (
            <>
              <p className="pick-sheet__zone-ok">
                <FiCheckCircle aria-hidden /> Zone{' '}
                {state.acceptedZone?.name || expectedZoneName}
              </p>
              <h2>
                Scan each unit of <strong>{line.productName}</strong>
              </h2>
            </>
          )}
          {busy && state.step !== 'grab' && (
            <p className="pick-sheet__checking">Checking…</p>
          )}
          {!state.quarantine && state.step !== 'grab' && state.step !== 'scan' && skipTarget && (
            <div className="pick-sheet__skip">
              <button
                type="button"
                className="pick-sheet__skip-button"
                disabled={busy}
                onClick={() => skipZone(skipTarget)}
              >
                <FiSkipForward aria-hidden /> {skipTarget.name} is empty — skip to the
                next zone
              </button>
              {state.step === 'unit' && state.acceptedZone ? (
                <button
                  type="button"
                  className="pick-sheet__skip-button pick-sheet__quarantine-button"
                  disabled={busy}
                  onClick={() => apply({ type: 'openQuarantine', zone: state.acceptedZone! })}
                >
                  <FiAlertTriangle aria-hidden /> Quarantine — missing or damaged
                </button>
              ) : (
                <small className="pick-sheet__quarantine-hint">
                  Missing or damaged stock? Scan the zone label first, then Quarantine.
                </small>
              )}
              {skippedLabel && <small>{skippedLabel}</small>}
            </div>
          )}
        </div>

        <div className="pick-sheet__scanner">
          <SharedScanner
            handleScan={handleScan}
            handleManualEntry={() => apply({ type: 'openManual' })}
            // A unit-scan line's units carry serials no manual entry can type: the 2D is required.
            hasManualEntry={
              !state.quarantine &&
              (state.step === 'zone' || (state.step === 'unit' && !unitScan))
            }
            rearmDelayMs={state.step === 'scan' ? UNIT_SCAN_REARM_DELAY_MS : undefined}
            scanProfile={
              SCAN_PROFILE_BY_KEY[
                pickScanProfileKey(state.step, line.kind, state.quarantine?.mode ?? null)
              ]
            }
          />
        </div>

        {state.step === 'grab' && (
          <PickGrabPanel
            line={line}
            zoneId={grabZoneId}
            stops={stops}
            busy={busy}
            shortEntryOpen={state.shortEntryOpen}
            onDone={onClose}
            onOpenShortEntry={() => apply({ type: 'openShortEntry' })}
            onCloseShortEntry={() => apply({ type: 'closeShortEntry' })}
            onSubmitShort={submitShort}
          />
        )}

        {state.step === 'scan' && (
          <PickUnitScanPanel
            line={line}
            zoneId={grabZoneId}
            stops={stops}
            busy={busy}
            shortEntryOpen={state.shortEntryOpen}
            onDone={onClose}
            onUndoLast={() => void removeScannedUnit(null)}
            onRemoveSerial={(serialNumber) => void removeScannedUnit(serialNumber)}
            onZoneShort={zoneShortFromScans}
            onOpenShortEntry={() => apply({ type: 'openShortEntry' })}
            onCloseShortEntry={() => apply({ type: 'closeShortEntry' })}
            onRecordShort={(reason) => void recordShort(reason)}
          />
        )}
      </div>

      {state.manualOpen && state.step !== 'grab' && state.step !== 'scan' && (
        <PickManualEntry
          mode={state.step === 'zone' ? 'zone' : 'unit'}
          kind={line.kind}
          productName={line.productName}
          expectedZoneName={expectedZoneName}
          busy={busy}
          onSubmitZone={(text) => submitZone(text, { isScan: false, productLike: false })}
          onSubmitUnit={submitManualUnit}
          onClose={() => apply({ type: 'closeManual' })}
        />
      )}
    </div>
  );
}

export default PickLineSheet;
